import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the saved Instant Quote CRUD page in a tab.
 *
 * Important:
 * - the component name keeps the existing typo "IntantQuotesConfig" to avoid
 *   breaking current imports/routes.
 */
const INSTANT_QUOTES_CRUD_ROUTE = "instant-quotes/instantQuotesConfigCrud";

//endregion

//region Component

/**
 * Renders the saved Instant Quotes configuration tab.
 *
 * Responsibilities:
 * - render the common configuration-page layout in tabs mode;
 * - keep title and subtitle visually compact through config-page.css;
 * - render the saved Instant Quotes MTable;
 * - keep Create/Edit/Copy/Print hidden;
 * - keep View handled by a dedicated tab-based workflow;
 * - leave Delete handled by MTable permissions/default behavior.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Saved Instant Quotes configuration tab.
 */
export default function IntantQuotesConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Removes table actions that are not required in this workflow.
     *
     * Business rule:
     * - saved Instant Quotes expose View;
     * - Delete remains handled by MTable when permissions allow it;
     * - Create/Edit/Copy/Print are intentionally hidden.
     */
    const removeContextItems = useMemo(
        () => ["Create", "Update", "Print", "Copy"],
        []
    );

    /**
     * Opens the saved Instant Quote detail tab.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates that a row exists because this page only supports View;
     * - persists the selected InstantQuote row for the CRUD/detail page;
     * - opens the configured route with a localized tab label.
     *
     * @param {"View"} operation - Requested operation.
     * @param {object|null|undefined} row - Selected saved quote row.
     * @returns {void}
     */
    const openRateOfferCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (!row) {
                toastMessages("error", "", t("SelectedLine"));
                return;
            }

            dispatch(
                M_StateActions.addM_State({
                    key: "InstantQuoteLine",
                    value: row,
                })
            );

            const tabLabel = t("LabelViewInstantQuote", {
                name: row?.Name ?? "",
            });

            onCreateTab(INSTANT_QUOTES_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default View behavior with the saved Instant Quote tab workflow.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openRateOfferCrud("View", row)],
            ]),
        [openRateOfferCrud]
    );

    return (
        <div
            className="config-page config-page--tabs"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header config-page__header--tabs">
                <div className="config-page__title config-page__title--tabs">
                    {t("InstantQuotesSaved")}
                </div>

                <div className="config-page__description config-page__description--tabs">
                    {t("InfoInstantQuotesSaved")}
                </div>
            </div>

            <div className="config-page__table">
                <MTable
                    filtersOnTable
                    entityName="RateOffers"
                    roleName="InstantQuotes"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                    tabsRailWidth={250}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

IntantQuotesConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
};

//endregion